import * as React from 'react';
import classNames from 'styles/utils/classNames';
import { FeedTypeToggle } from 'components/OptionAggregatedTable/styles';

export interface TimeframeOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: TimeframeOption[];
  disabled?: boolean;
  className?: string;
}

// Single-select segmented toggle for panel headers. Reuses FeedTypeToggle (the
// same control as the Options/Dark Pool switch) and behaves as a radiogroup.
export default function TimeframeToggle({
  value,
  onChange,
  options,
  disabled = false,
  className,
}: Props) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled) return;
    const lastIndex = options.length - 1;
    let nextIndex: number | null = null;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = index === lastIndex ? 0 : index + 1;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = index === 0 ? lastIndex : index - 1;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = lastIndex;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      onChange(options[nextIndex].value);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Timeframe"
      className={classNames('flex items-center', className)}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        return (
          <FeedTypeToggle
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            isActive={isActive}
            disabled={disabled}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => handleKeyDown(e, index)}
          >
            {option.label}
          </FeedTypeToggle>
        );
      })}
    </div>
  );
}
